import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Add this import for navigation
import SearchBar from "./components/career/SearchBar";
import JobCard from "./components/career/JobCard";
import Pagination from "./components/career/Pagination";
import Header from "./components/career/Header";
import HeaderBanner from "./components/career/HeaderBanner";
import CompanyService from "./services/CompanyService";
import { API_BASE_URL } from "./utils/api";

const JobListings = ({ companyId }) => {
  const navigate = useNavigate(); // Add this hook for navigation
  const [searchTerm, setSearchTerm] = useState("");
  const [department, setDepartment] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [jobType, setJobType] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 18;
  const [isSearching, setIsSearching] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState(jobs);
  const [company, setCompany] = useState({});

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      let result = [...jobs];

      // Search filter
      if (searchTerm) {
        setIsSearching(true);
        result = result.filter((job) =>
          job.jobAdvert.basicInfo.jobTitle
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        );
        setIsSearching(false);
      }

      // Department filter
      if (department !== "") {
        result = result.filter((job) => job.department === department);
      }

      // Location filter
      if (locationFilter !== "") {
        result = result.filter((job) => {
          const location = job.jobAdvert.basicInfo.location.details;
          const address =
            `${location.city} ${location.country} ${location.postcode} ${location.postcode}`.toLowerCase();
          if (address.includes(locationFilter.toLowerCase())) {
            return true;
          }
          return false;
        });
      }

      // Job type filter
      if (jobType !== "") {
        result = result.filter((job) => job.type === jobType);
      }

      // Experience level filter
      if (experienceLevel !== "") {
        result = result.filter(
          (job) => job.experienceLevel === experienceLevel,
        );
      }

      // Sorting
      if (sortBy === "newest") {
        result.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      } else if (sortBy === "titleAsc" || sortBy === "titleDesc") {
        result.sort((a, b) => {
          // Convert titles to lowercase for case-insensitive comparison
          const titleA = a.jobAdvert.basicInfo.jobTitle.toLowerCase();
          const titleB = b.jobAdvert.basicInfo.jobTitle.toLowerCase();
          // For ascending order
          if (sortBy.toLowerCase() === "titleasc") {
            return titleA.localeCompare(titleB);
          }
          // For descending order
          else if (sortBy.toLowerCase() === "titledesc") {
            return titleB.localeCompare(titleA);
          }
          // Default to ascending if invalid direction is provided
          else {
            return titleA.localeCompare(titleB);
          }
        });
      } else {
        result.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      }

      setFilteredJobs(result);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    searchTerm,
    department,
    locationFilter,
    jobType,
    experienceLevel,
    sortBy,
    jobs,
  ]);

  useEffect(() => {
    const companyDetails = async () => {
      try {
        const company = await CompanyService.getCompany(companyId);
        setCompany(company);
        setJobs(company.jobs);

        // Check if career page exists and is inactive
        let careerPageInactive = true;
        if (company.settings && company.settings.pages) {
          if (company.settings.pages.length === 0) {
            careerPageInactive = true;
          } else {
            careerPageInactive = (company?.settings?.pages ?? []).some(
              (page) => page.name === "career" && page.status === false,
            );
          }
        }

        // If career page is found and is inactive, redirect to 404
        if (careerPageInactive) {
          navigate("/404");
        }
      } catch (error) {
        console.error("Error fetching company details:", error);
        // Optionally redirect to error page if company fetch fails
        // navigate('/error');
      }
    };
    companyDetails();
  }, [companyId, navigate]);

  // Pagination calculations
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (opt) => {
    const { keyword, location, sortOption } = opt;
    setSearchTerm(keyword);
    setLocationFilter(location);
    setSortBy(sortOption);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        logoPath={company && company.logo ? API_BASE_URL + company.logo : null}
      />
      <HeaderBanner
        title="Build Your Career With Us"
        subtitle={
          company.description ??
          `Explore opportunities and join our team of professionals`
        }
        logoUrl={company && company.logo ? API_BASE_URL + company.logo : null}
        category="Careers"
      />

      <main className="container px-4 py-8 md:py-10 m-auto">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Open Positions</h2>
          <p className="text-gray-600">Find your next role at {company.name}</p>
        </div>

        <SearchBar onSubmit={handleSearch} />
        <div
          className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-4 ${isSearching ? "opacity-60" : ""} p-10`}
        >
          {currentJobs.map((job) => (
            <JobCard key={job._id} job={job} featured={job.featured ?? false} />
          ))}
        </div>
        {currentJobs.length === 0 ? (
          <div className="w-full text-center">No live Jobs were found</div>
        ) : (
          ""
        )}
        {currentJobs.length > 0 ? (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        ) : (
          ""
        )}
      </main>
    </div>
  );
};

export default JobListings;
